import { useState, useEffect } from 'react';
import { XMarkIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import api from '../services/api';


const getDisplayName = (name) => {
  if (!name) return '';
  if (typeof name === 'string') return name;
  if (typeof name === 'object') {
    const values = Object.values(name);
    return values.find(v => v) || '';
  }
  return String(name);
};

export default function FilamentForm({ filament, onSave, onCancel, barcodeInput = '', ownership = 'operator' }) {
  const [materials, setMaterials] = useState([]);
  const [materialExtra, setMaterialExtras] = useState([]);
  const [brands, setBrands] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    material: '',
    materialExtra: null,
    color: { name: '', hex: '' },
    diameter: 1.75,
    weight: 1000,
    barCodes: barcodeInput ? [barcodeInput] : [],
    currentStock: 1000,
    minimumStock: 0,
    costKg: 0,
    notes: '',
    suppliers: [{ name: '', contact: '' }],
    showInSite: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchMaterials();
    fetchMaterialExtras();
    fetchBrands();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch on mount only
  }, []);

  useEffect(() => {
    if (filament) {
      setFormData({
        name: filament.name || '',
        brand: filament.brand?._id || filament.brand || '',
        material: typeof filament.material === 'object' ? filament.material._id : filament.material,
        materialExtra: filament.materialExtra ? (typeof filament.materialExtra === 'object' ? filament.materialExtra._id : filament.materialExtra) : null,
        color: filament.color || { name: '', hex: '' },
        diameter: filament.diameter || 1.75,
        weight: filament.weight || 1000,
        barCodes: filament.barCodes && filament.barCodes.length > 0 ? filament.barCodes : [],
        currentStock: filament.currentStock || 0,
        minimumStock: filament.minimumStock || 0,
        costKg: filament.costKg || 0,
        notes: filament.notes || '',
        suppliers: filament.suppliers && filament.suppliers.length > 0
          ? filament.suppliers
          : [{ name: '', contact: '' }],
        showInSite: filament.showInSite ?? true
      });
    }
  }, [filament]);

  const fetchMaterials = async () => {
    try {
      const response = await api.get(`/auth/materials?ownership=${ownership}`);
      setMaterials(response.data.data || []);
    } catch (err) {
      console.error('Failed to fetch materials:', err);
    }
  };

  const fetchMaterialExtras = async () => {
    try {
      const response = await api.get(`/auth/material-extras?ownership=${ownership}`);
      setMaterialExtras(response.data.data || []);
    } catch (err) {
      console.error('Failed to fetch material extras:', err);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await api.get(`/auth/filament-brands?ownership=${ownership}`);
      setBrands(response.data.data || []);
    } catch (err) {
      console.error('Failed to fetch brands:', err);
    }
  };

  // const handleBarcodeLookup = async () => {
  //   if (!barcodeSearch.trim()) {
  //     setBarcodeError('Please enter a barcode');
  //     return;
  //   }

  //   setBarcodeLoading(true);
  //   setBarcodeError('');

  //   try {
  //     const response = await api.get(`/auth/filaments/barcode/${barcodeSearch.trim()}`);
  //     const foundFilament = response.data.data;

  //     if (foundFilament) {
  //       // Pre-fill the form with the found filament data
  //       setFormData({
  //         name: foundFilament.name || '',
  //         brand: foundFilament.brand || '',
  //         material: typeof foundFilament.material === 'object' ? foundFilament.material._id : foundFilament.material,
  //         materialExtra: foundFilament.materialExtra?.map(me => typeof me === 'object' ? me._id : me) || [],
  //         color: foundFilament.color || { name: '', hex: '' },
  //         diameter: foundFilament.diameter || 1.75,
  //         weight: foundFilament.weight || 1000,
  //         barCodes: foundFilament.barCodes && foundFilament.barCodes.length > 0 ? foundFilament.barCodes : [''],
  //         currentStock: foundFilament.currentStock || 0,
  //         minimumStock: foundFilament.minimumStock || 0,
  //         costKg: foundFilament.costKg || 0,
  //         suppliers: foundFilament.suppliers && foundFilament.suppliers.length > 0
  //           ? foundFilament.suppliers
  //           : [{ name: '', contact: '' }],
  //         showInSite: foundFilament.showInSite ?? true
  //       });
  //       setBarcodeError('');
  //       alert(`Filament found: ${foundFilament.name}. Form has been pre-filled.`);
  //     }
  //   } catch (err) {
  //     if (err.response?.status === 404) {
  //       setBarcodeError('Filament not found with this barcode');
  //     } else {
  //       setBarcodeError(err.response?.data?.message || 'Failed to lookup barcode');
  //     }
  //   } finally {
  //     setBarcodeLoading(false);
  //   }
  // };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Build payload with only valid API fields
      const dataToSave = {
        name: formData.name,
        brand: formData.brand,
        material: formData.material,
        materialExtra: formData.materialExtra || null,
        color: formData.color,
        diameter: parseFloat(formData.diameter),
        weight: parseInt(formData.weight, 10),
        barCodes: formData.barCodes.filter(b => b.trim()),
        currentStock: parseInt(formData.currentStock, 10),
        minimumStock: parseInt(formData.minimumStock, 10),
        costKg: parseFloat(formData.costKg),
        suppliers: formData.suppliers.filter(s => s.name?.trim() || s.contact?.trim()),
        notes: formData.notes || '',
        showInSite: formData.showInSite
      };
      await onSave(dataToSave);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save filament');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleColorChange = (field, value) => {
    setFormData({
      ...formData,
      color: { ...formData.color, [field]: value }
    });
  };

  const handleMaterialExtraChange = (e) => {
    const value = e.target.value || null;
    setFormData({ ...formData, materialExtra: value });
  };

  const handleSupplierChange = (index, field, value) => {
    const newSuppliers = [...formData.suppliers];
    newSuppliers[index] = { ...newSuppliers[index], [field]: value };
    setFormData({ ...formData, suppliers: newSuppliers });
  };

  const addSupplier = () => {
    setFormData({
      ...formData,
      suppliers: [...formData.suppliers, { name: '', contact: '' }]
    });
  };

  const removeSupplier = (index) => {
    const newSuppliers = formData.suppliers.filter((_, i) => i !== index);
    setFormData({ ...formData, suppliers: newSuppliers });
  };

  const handleBarCodeChange = (index, value) => {
    const newBarCodes = [...formData.barCodes];
    newBarCodes[index] = value;
    setFormData({ ...formData, barCodes: newBarCodes });
  };

  const addBarCode = () => {
    setFormData({
      ...formData,
      barCodes: [...formData.barCodes, '']
    });
  };

  const removeBarCode = (index) => {
    const newBarCodes = formData.barCodes.filter((_, i) => i !== index);
    setFormData({ ...formData, barCodes: newBarCodes });
  };

  return (
    <div className="fixed inset-0 bg-slate-900 bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
          <h2 className="text-2xl font-bold text-slate-900">
            {filament ? 'Edit Filament' : 'Add Filament'}
          </h2>
          <button
            onClick={onCancel}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="px-6 py-6 space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Barcode Lookup
            {!filament && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <label className="block text-sm font-medium text-blue-900 mb-2">
                  Quick Lookup by Barcode
                </label>
                <p className="text-xs text-blue-700 mb-3">
                  Scan or enter a barcode to auto-fill the form with existing filament data
                </p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={barcodeSearch}
                    onChange={(e) => setBarcodeSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleBarcodeLookup();
                      }
                    }}
                    disabled={barcodeLoading}
                    placeholder="Enter or scan barcode..."
                    className="flex-1 px-4 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                  />
                  <button
                    type="button"
                    onClick={handleBarcodeLookup}
                    disabled={barcodeLoading}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 font-medium"
                  >
                    <MagnifyingGlassIcon className="h-5 w-5 mr-1" />
                    {barcodeLoading ? 'Searching...' : 'Lookup'}
                  </button>
                </div>
                {barcodeError && (
                  <p className="mt-2 text-sm text-red-600">{barcodeError}</p>
                )}
              </div>
            )} */}

            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  maxLength={100}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                />
              </div>

              <div>
                <label htmlFor="brand" className="block text-sm font-medium text-slate-700 mb-2">
                  Brand *
                </label>
                <select
                  id="brand"
                  name="brand"
                  value={formData.brand}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100 bg-white"
                >
                  <option value="">Select brand...</option>
                  {brands.map(b => (
                    <option key={b._id} value={b._id} selected={b._id === formData.brand}>{getDisplayName(b.name)}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Material Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="material" className="block text-sm font-medium text-slate-700 mb-2">
                  Material *
                </label>
                <select
                  id="material"
                  name="material"
                  value={formData.material}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100 bg-white"
                >
                  <option value="">Select material...</option>
                  {materials.map(m => (
                    <option key={m._id} value={m._id}>{getDisplayName(m.name)} ({m.key})</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="materialExtra" className="block text-sm font-medium text-slate-700 mb-2">
                  Material Extra
                </label>
                <select
                  id="materialExtra"
                  name="materialExtra"
                  value={formData.materialExtra || ''}
                  onChange={handleMaterialExtraChange}
                  disabled={loading}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100 bg-white"
                >
                  <option value="">None</option>
                  {materialExtra.map(me => (
                    <option key={me._id} value={me._id}>{getDisplayName(me.name)} ({me.key})</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Color */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="colorName" className="block text-sm font-medium text-slate-700 mb-2">
                  Color Name
                </label>
                <input
                  type="text"
                  id="colorName"
                  value={formData.color.name}
                  onChange={(e) => handleColorChange('name', e.target.value)}
                  disabled={loading}
                  maxLength={100}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                />
              </div>

              <div>
                <label htmlFor="colorHex" className="block text-sm font-medium text-slate-700 mb-2">
                  Color Hex (without #)
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    id="colorHex"
                    value={formData.color.hex}
                    onChange={(e) => handleColorChange('hex', e.target.value.replace('#', ''))}
                    disabled={loading}
                    maxLength={6}
                    pattern="[0-9A-Fa-f]{6}"
                    placeholder="FF0000"
                    className="flex-1 px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                  />
                  {formData.color.hex && (
                    <div
                      style={{ backgroundColor: `#${formData.color.hex}` }}
                      className="w-12 h-11 border border-slate-300 rounded-lg shadow-inner"
                    ></div>
                  )}
                </div>
              </div>
            </div>

            {/* Specs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label htmlFor="diameter" className="block text-sm font-medium text-slate-700 mb-2">
                  Diameter (mm)
                </label>
                <select
                  id="diameter"
                  name="diameter"
                  value={formData.diameter}
                  onChange={handleChange}
                  disabled={loading}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100 bg-white"
                >
                  <option value={1.75}>1.75</option>
                  <option value={2.85}>2.85</option>
                </select>
              </div>

              <div>
                <label htmlFor="weight" className="block text-sm font-medium text-slate-700 mb-2">
                  Weight (g)
                </label>
                <input
                  type="number"
                  id="weight"
                  name="weight"
                  value={formData.weight}
                  onChange={handleChange}
                  disabled={loading}
                  min={0}
                  max={10000}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                />
              </div>

              <div>
                <label htmlFor="currentStock" className="block text-sm font-medium text-slate-700 mb-2">
                  Current Stock (g)
                </label>
                <input
                  type="number"
                  id="currentStock"
                  name="currentStock"
                  value={formData.currentStock}
                  onChange={handleChange}
                  disabled={loading}
                  min={0}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                />
              </div>

              <div>
                <label htmlFor="minimumStock" className="block text-sm font-medium text-slate-700 mb-2">
                  Min Stock (g)
                </label>
                <input
                  type="number"
                  id="minimumStock"
                  name="minimumStock"
                  value={formData.minimumStock}
                  onChange={handleChange}
                  disabled={loading}
                  min={0}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                />
              </div>
            </div>

            {/* Bar Codes */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-slate-700">Bar Codes (Optional)</label>
              {formData.barCodes.length > 0 ? (
                formData.barCodes.map((barCode, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={barCode}
                      onChange={(e) => handleBarCodeChange(index, e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                      disabled={loading}
                      placeholder="Enter barcode (EAN-13, UPC-A, etc.)"
                      className="flex-1 px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                    />
                    <button
                      type="button"
                      onClick={() => removeBarCode(index)}
                      disabled={loading}
                      className="px-3 py-2 text-sm bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                ))
              ) : (
                <p className="text-sm text-slate-500 italic">No barcodes added</p>
              )}
              <button
                type="button"
                onClick={addBarCode}
                disabled={loading}
                className="inline-flex items-center px-4 py-2 text-sm bg-green-100 hover:bg-green-200 text-green-700 rounded-lg transition-colors font-medium"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Bar Code
              </button>
            </div>

            {/* Pricing */}
            <div className="space-y-4">
              <label className="block text-sm font-semibold text-slate-800">Pricing Information</label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="costKg" className="block text-sm font-medium text-slate-700 mb-2">
                    Cost per Kg (€) *
                  </label>
                  <input
                    type="number"
                    id="costKg"
                    name="costKg"
                    value={formData.costKg}
                    onChange={handleChange}
                    required
                    disabled={loading}
                    min={1}
                    step="0.01"
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                  />
                </div>

                {/* <div>
                  <label htmlFor="pricePerPrintedKg" className="block text-sm font-medium text-slate-700 mb-2">
                    Price per Printed Kg (€)
                  </label>
                  <input
                    type="number"
                    id="pricePerPrintedKg"
                    name="pricePerPrintedKg"
                    value={formData.pricePerPrintedKg}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      setFormData({
                        ...formData,
                        pricePerPrintedKg: value
                        // pricePerPrintedKgIVA: (value * 1.23).toFixed(2)
                      });
                    }}
                    disabled={loading}
                    min={0}
                    step="0.01"
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                  />
                </div>

                <div>
                  <label htmlFor="pricePerPrintedKgIVA" className="block text-sm font-medium text-slate-700 mb-2">
                    Price with IVA (€)
                  </label>
                  <input
                    type="number"
                    id="pricePerPrintedKgIVA"
                    name="pricePerPrintedKgIVA"
                    value={formData.pricePerPrintedKgIVA}
                    disabled
                    step="0.01"
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg bg-slate-100 text-slate-600"
                  />
                  <p className="text-xs text-slate-500 mt-1">Auto-calculated (23% VAT)</p>
                </div> */}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-slate-700 mb-2">
                Notes
              </label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                disabled={loading}
                maxLength={500}
                rows={3}
                placeholder="Additional notes about this filament..."
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100 resize-none"
              />
              <p className="text-xs text-slate-500 mt-1">{formData.notes?.length | 0}/500 characters</p>
            </div>

            {/* Show in Site */}
            <div className="flex items-center">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="showInSite"
                  checked={formData.showInSite}
                  onChange={handleChange}
                  disabled={loading}
                  className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-slate-700">Show in Site</span>
              </label>
            </div>

            {/* Suppliers */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-slate-700">Suppliers</label>
              {formData.suppliers.map((supplier, index) => (
                <div key={index} className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Supplier Name</label>
                      <input
                        type="text"
                        value={supplier.name}
                        onChange={(e) => handleSupplierChange(index, 'name', e.target.value)}
                        disabled={loading}
                        maxLength={100}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Contact</label>
                      <input
                        type="text"
                        value={supplier.contact}
                        onChange={(e) => handleSupplierChange(index, 'contact', e.target.value)}
                        disabled={loading}
                        maxLength={100}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100 text-sm"
                      />
                    </div>
                  </div>
                  {formData.suppliers.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeSupplier(index)}
                      disabled={loading}
                      className="mt-3 inline-flex items-center px-3 py-1.5 text-sm bg-red-100 hover:bg-red-200 text-red-700 rounded-md transition-colors"
                    >
                      <TrashIcon className="h-4 w-4 mr-1" />
                      Remove
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addSupplier}
                disabled={loading}
                className="inline-flex items-center px-4 py-2 text-sm bg-green-100 hover:bg-green-200 text-green-700 rounded-lg transition-colors font-medium"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Supplier
              </button>
            </div>

          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="px-5 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 transition-colors shadow-sm"
            >
              {loading ? 'Saving...' : 'Save Filament'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
